import { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import Paper from '@mui/material/Paper';
import { getCoreRowModel } from '@tanstack/react-table';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { MenuItem, Select } from '@mui/material';

// Utils
import { Storage } from '../../../utils/Storage';
import { FormatForApi, shortDateFormat } from '../../../utils/Format';
import { makeLucTransferReceiptColumns } from '../../../utils/columns/makeLucTransferReceiptColumns';

// Services
import { getLucTransfersReceipts } from '../../../services/luc-transfer.service';

// Types
import { FilterableTypes } from '../../../types/FilterableTypes';
import { LUCTransferReceipt } from '../../../types/luc-transfer-receipt';
import { SearchBarFilterTypes } from '../../../constants/Constants';
import { LUCTransferType } from '../../../types/luc-transfer';
import { ColumnExtendedStrict } from '../../../types/ColumnExtended';

// Commmon
import SearchBar from '../../common/SearchBar/SearchBar';
import { Table, useNewTable } from '../../common/table/index';
import FormInputCollection from '../../common/FormInputCollection/FormInputCollection';

export const Transfers = () => {
  const [timestampFrom, setTimestampFrom] = useState<Date | null>(null);
  const [timestampTo, setTimestampTo] = useState<Date | null>(null);
  const [paramFilter, setParamFilter] = useState<any>({
    sortBy: [{ timestamp: '-1' }],
  });
  const [isResetting, setIsResetting] = useState(false);
  const [transferType, setTransferType] = useState<LUCTransferType | null>(null);

  useEffect(() => {
    if (isResetting) {
      // Reset paramFilter and clear the resetting flag
      setParamFilter({ filters: [], sortBy: paramFilter.sortBy });
      setIsResetting(false);
    }
  }, [isResetting, paramFilter.sortBy]);

  const handleReset = () => {
    setIsResetting(true);
    setTimestampFrom(null);
    setTimestampTo(null);
    setTransferType(null);
    setParamFilter({ filters: [], sortBy: paramFilter.sortBy });
    Storage.setSearchBarFiltering(SearchBarFilterTypes.LUC_TRANSFERS, { filters: [] });
  };

  const columns = useMemo(() => makeLucTransferReceiptColumns(), []);

  // These are custom search columns. Why aren't we simply using the columns from makeLucTransferReceiptColumns?
  // Well, because we're overriding these columns below in onSearchChange. We want users to be able to search for
  // both transferringPartyName and receivingPartyName using the same search field. We also want users to be able
  // to search for transferringFromStationIataCode and transferringToStationIataCode using the same search field.
  const searchColumns: ColumnExtendedStrict<LUCTransferReceipt>[] = [
    {
      header: 'Control Receipt No.',
      accessorKey: 'controlReceiptNr',
      enableColumnFilter: true,
    },
    {
      header: 'Broker Code',
      accessorKey: 'transferringPartyName',
      enableColumnFilter: true,
    },
    {
      header: 'Station',
      accessorKey: 'transferringFromStationIataCode',
      enableColumnFilter: true,
    },
  ];

  const table = useNewTable<LUCTransferReceipt>({
    columns,
    fetchAsync: getLucTransfersReceipts,
    params: paramFilter,
    serverSide: true,
    shouldFetch: true,
    initialState: {
      sorting: [
        {
          id: 'createdDateTime',
          desc: true,
        },
      ],
    },
  });

  const onSearchChange = (filters: any[]) => {
    // If user is searching for transferring party and/or station, also search for receiving
    // party and/or station at the same time.
    for (const filter of filters) {
      if (Object.hasOwn(filter, 'transferringPartyName')) {
        filter.or = {
          receivingPartyName: filter.transferringPartyName,
        };
      }

      if (Object.hasOwn(filter, 'transferringFromStationIataCode')) {
        filter.or = {
          transferringToStationIataCode: filter.transferringFromStationIataCode,
        };
      }
    }

    // Transfer type filter
    if (transferType != null) {
      filters.push({
        type: transferType,
        filterType: FilterableTypes.Equals,
      });
    }

    // Timestamp filter
    if (timestampFrom && timestampTo) {
      filters.push({
        timestamp: FormatForApi(moment(timestampFrom), true),
        filterType: FilterableTypes.GreaterThanOrEqual,
        and: {
          timestamp: FormatForApi(moment(timestampTo), false),
          filterType: FilterableTypes.LessThanOrEqual,
        },
      });
    }
    setParamFilter({ filters: filters });
    Storage.setSearchBarFiltering(SearchBarFilterTypes.LUC_TRANSFERS, { isMoreFilter: true, filters: filters });
  };

  return (
    <>
      <SearchBar
        pageKey={SearchBarFilterTypes.LUC_TRANSFERS}
        onSearch={onSearchChange}
        onReset={handleReset}
        columns={searchColumns}
        allowSearchAll={true}
        components={
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <FormInputCollection label='Type'>
              <Select
                variant='outlined'
                id='typeId'
                name='typeId'
                value={transferType ?? 'all'}
                size='small'
                style={{ width: '150px' }}
                onChange={(e: any) => {
                  setTransferType(e.target.value === 'all' ? null : (Number(e.target.value) as LUCTransferType));
                }}
              >
                <MenuItem value={'all'}>All</MenuItem>
                {Object.values(LUCTransferType)
                  .splice(Object.values(LUCTransferType).length / 2)
                  ?.map((val, index: number) => {
                    return (
                      <MenuItem key={index} value={val}>
                        {LUCTransferType[val]}
                      </MenuItem>
                    );
                  })}
              </Select>
            </FormInputCollection>

            <FormInputCollection label={`Transfer Timestamp`} style={{ marginLeft: '24px', marginBottom: '8px' }}>
              <div style={{ marginRight: '8px' }}>
                <DatePicker
                  label='From'
                  value={timestampFrom ? moment(timestampFrom) : null}
                  format={shortDateFormat}
                  sx={{ width: '185px' }}
                  slotProps={{ textField: { size: 'small' } }}
                  onChange={(date) => setTimestampFrom(date ? date.toDate() : null)}
                />
              </div>
              <DatePicker
                value={timestampTo ? moment(timestampTo) : null}
                label='To'
                format={shortDateFormat}
                sx={{ width: '185px' }}
                slotProps={{ textField: { size: 'small' } }}
                onChange={(date) => setTimestampTo(date ? date.toDate() : null)}
              />
            </FormInputCollection>
          </LocalizationProvider>
        }
      />
      <Paper>
        <Table {...table} showSearchBar={false} showColumnToggler={false} getCoreRowModel={getCoreRowModel()} />
      </Paper>
    </>
  );
};
